import { API_URL } from '../api';

const FALLBACK_ORIGIN = 'https://guhso.com';

/**
 * Origin that serves uploaded media (episode art, post covers, product shots).
 *
 * This used to be derived purely by stripping `/api/v1` off API_URL, which
 * breaks in two ways:
 *
 *  1. When the API is same-origin (`REACT_APP_API_URL=/api/v1`, as the CRA dev
 *     proxy needs), stripping leaves an empty string, so every image resolved
 *     against the dev server and 404'd.
 *  2. Media does not necessarily live on the API host at all.
 *
 * `REACT_APP_MEDIA_URL` now takes precedence, and an empty derived origin falls
 * back rather than producing same-origin paths that cannot exist.
 */
export const getApiOrigin = () => {
  const explicit = process.env.REACT_APP_MEDIA_URL;
  if (explicit) {
    return explicit.replace(/\/+$/, '');
  }

  const base = API_URL || process.env.REACT_APP_API_URL || FALLBACK_ORIGIN;

  const derived = base
    .replace(/\/api\/v1$/i, '')
    .replace(/\/api$/i, '')
    .replace(/\/v1$/i, '')
    .replace(/\/+$/, '');

  // A relative API URL strips down to '' — that would make every image a
  // same-origin path on the frontend, where uploads do not exist.
  return derived || FALLBACK_ORIGIN;
};

export const resolveImageUrl = (imagePath, options = {}) => {
  const { defaultUrl = null, baseOrigin = getApiOrigin() } = options;

  if (!imagePath) {
    return defaultUrl;
  }

  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  if (imagePath.startsWith('//')) {
    return `https:${imagePath}`;
  }

  const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
  return `${baseOrigin}${normalizedPath}`;
};
